// The branded chat page, jawabai.cloud/c/<slug> (docs/branded-page.md).
//
// A business's own page: its name, picture, colours, contact details and its
// AI agent's chat. Rendered OUTSIDE the marketing layout (App.jsx) — no
// jawabai header, footer or chat bubble — and in the business's colours, all
// derived by lib/brandPageTheme.js so every text stays readable.
//
// Language: the business's setting, or for `auto` the visitor's browser. The
// page uses a fixed translator and never calls i18n.changeLanguage, which
// would rewrite the visitor's saved language for every jawabai site.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import i18n from '../lib/i18n';
import { brandPageTheme } from '../lib/brandPageTheme';
import {
  brandImageUrl,
  fetchBrandPage,
  loadTranscript,
  saveTranscript,
  sendBrandPageMessage,
} from '../lib/brandPageApi';
import { clockIn, isOpenAt } from '../lib/openingHours';
import {
  Avatar,
  Chip,
  Composer,
  Hero,
  InfoCardCompact,
  InfoSections,
  LinkButtons,
  Messages,
  PoweredBy,
  contactLinks,
} from '../components/brandPage/parts';
import ReportDialog, { ReportLink } from '../components/brandPage/ReportDialog';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

function pageLanguage(setting) {
  if (setting === 'ar' || setting === 'en') return setting;
  const langs = navigator.languages?.length ? navigator.languages : [navigator.language || ''];
  for (const l of langs) {
    if (/^ar\b/i.test(l)) return 'ar';
    if (/^en\b/i.test(l)) return 'en';
  }
  return 'ar'; // the product's default language
}

// Today's hours and whether the shop is open, re-checked every minute.
function useHours(page) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  return useMemo(() => {
    const week = Array.isArray(page?.hours) ? page.hours : [];
    const has = week.some((d) => d.open);
    const now = clockIn(page?.timezone || 'Asia/Damascus');
    return {
      has,
      week,
      todayIndex: now.day,
      today: week.find((d) => d.day === now.day),
      openNow: has && isOpenAt(week, now),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, tick]);
}

export default function BrandPage() {
  const { slug: rawSlug = '' } = useParams();
  const slug = rawSlug.toLowerCase();
  const [state, setState] = useState({ status: 'loading' });

  const load = useCallback(() => {
    setState({ status: 'loading' });
    fetchBrandPage(slug)
      .then((r) =>
        setState(r.notFound ? { status: 'notFound' } : { status: 'ready', page: r.page }),
      )
      .catch(() => setState({ status: 'error' }));
  }, [slug]);

  useEffect(load, [load]);

  if (state.status === 'ready') return <Ready page={state.page} slug={slug} />;
  return <Fallback status={state.status} onRetry={load} />;
}

// Loading, error and not-found use the site's own look (the business's
// colours are not known yet, or there is no business).
function Fallback({ status, onRetry }) {
  const t = i18n.getFixedT(i18n.language, null, 'brandPage');
  useEffect(() => {
    if (status === 'notFound') document.title = t('notFoundTitle');
  }, [status, t]);
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg px-6 text-center text-text">
      {status === 'loading' && (
        <p className="text-text-muted" role="status">
          {t('loading')}
        </p>
      )}
      {status === 'error' && (
        <div className="flex flex-col items-center gap-4">
          <p className="text-lg font-semibold">{t('loadFailed')}</p>
          <button
            type="button"
            onClick={onRetry}
            className="rounded-lg bg-primary px-4 py-2 font-semibold text-primary-fg hover:bg-primary-hover"
          >
            {t('retry')}
          </button>
        </div>
      )}
      {status === 'notFound' && (
        <div className="flex max-w-sm flex-col items-center gap-3">
          <h1 className="text-2xl font-bold">{t('notFoundTitle')}</h1>
          <p className="text-text-muted">{t('notFoundBody')}</p>
          <a
            href="/"
            className="mt-2 rounded-lg bg-primary px-4 py-2 font-semibold text-primary-fg hover:bg-primary-hover"
          >
            {t('notFoundCta')}
          </a>
        </div>
      )}
    </div>
  );
}

// `preview`: drawn inside the dashboard's editor (BrandPagePreview) — no
// chat, nothing stored, so a half-edited page can never send or remember.
export function Ready({ page, slug, preview = false }) {
  const lang = pageLanguage(page.language);
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const t = useMemo(() => i18n.getFixedT(lang, null, 'brandPage'), [lang]);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const desktop = useMediaQuery('(min-width: 1024px)');
  const mode = page.themeMode === 'auto' ? (prefersDark ? 'dark' : 'light') : page.themeMode;
  const theme = useMemo(
    () =>
      brandPageTheme({
        brand: page.brandColor,
        accent: page.accentColor,
        mode,
        background: page.background,
        hasCover: !!page.coverImageId,
      }),
    [page, mode],
  );
  const hours = useHours(page);
  const links = useMemo(() => contactLinks(page, t), [page, t]);

  // The document follows the page: its language/direction (fonts and RTL
  // come from html[dir]), the tab title, and the browser colour scheme.
  useEffect(() => {
    const html = document.documentElement;
    const before = { lang: html.lang, dir: html.dir, title: document.title };
    html.lang = lang;
    html.dir = dir;
    document.title = page.name;
    return () => {
      html.lang = before.lang;
      html.dir = before.dir;
      document.title = before.title;
    };
  }, [lang, dir, page.name]);

  // ── chat state ──
  const [messages, setMessages] = useState(() => (preview ? [] : loadTranscript(slug)));
  const [draft, setDraft] = useState('');
  const [typing, setTyping] = useState(false);
  const [gone, setGone] = useState(false);
  const [reporting, setReporting] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!preview) saveTranscript(slug, messages);
  }, [slug, messages, preview]);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && (messages.length || typing))
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, typing]);

  const send = useCallback(
    async (text) => {
      const content = text.trim();
      if (preview || !content || typing || gone) return;
      setDraft('');
      setMessages((m) => [...m, { who: 'me', text: content }]);
      setTyping(true);
      try {
        const reply = await sendBrandPageMessage(slug, content);
        setMessages((m) => [
          ...m,
          { who: 'bot', text: reply.content || t('empty'), suggestions: reply.suggestions },
        ]);
      } catch (err) {
        if (err.code === 'gone') setGone(true);
        const key =
          { rate_limited: 'rateLimited', offline: 'offline', gone: 'gone' }[err.code] || 'failed';
        setMessages((m) => [...m, { who: 'error', text: t(key) }]);
      } finally {
        setTyping(false);
      }
    },
    [slug, t, typing, gone, preview],
  );

  const hasChips = page.suggestedQuestions?.length > 0;
  // Suggested questions under the latest answer (the agent's own, from its
  // knowledge). Only the last reply has them; sending anything hides them.
  const last = messages[messages.length - 1];
  const replySuggestions =
    !typing && !gone && last?.who === 'bot' && last.suggestions?.length ? last.suggestions : [];
  const chat = {
    page,
    messages,
    typing,
    t,
    desktop,
    onChip: send,
    showInlineChips: hasChips && messages.length === 0,
    replySuggestions,
  };
  // The owner's starter strip steps aside while the answer has its own buttons.
  const chipStrip =
    hasChips && messages.length > 0 && !typing && !gone && !replySuggestions.length;
  const composer = (
    <Composer
      value={draft}
      onChange={setDraft}
      onSubmit={() => send(draft)}
      disabled={preview || typing || gone}
      placeholder={page.placeholder || t('placeholder')}
      maxLength={page.maxMessageChars || 1000}
      t={t}
      lang={lang}
    />
  );

  // "Powered by jawabai" · "Report this page". The preview inside the
  // dashboard shows the mark but no report link: there is no page to report.
  const footer = (
    <>
      <PoweredBy show={page.showPoweredBy} t={t} />
      {page.showPoweredBy && !preview && <span className="text-[var(--text-muted)]">·</span>}
      {!preview && <ReportLink onClick={() => setReporting(true)} t={t} />}
    </>
  );

  const coverBackdrop = theme.background === 'cover' && page.coverImageId;
  const rootStyle = {
    ...theme.vars,
    colorScheme: theme.mode,
    background: 'var(--bg)',
    backgroundImage: 'var(--bg-image)',
    color: 'var(--text)',
  };

  return (
    <div
      dir={dir}
      lang={lang}
      className="brand-page relative h-dvh overflow-hidden"
      style={rootStyle}
    >
      {coverBackdrop && <CoverBackdrop page={page} />}
      {desktop ? (
        <DesktopLayout
          {...{
            page,
            hours,
            links,
            t,
            lang,
            chat,
            chipStrip,
            composer,
            scrollRef,
            send,
            footer,
          }}
        />
      ) : (
        <MobileLayout
          {...{
            page,
            hours,
            links,
            t,
            lang,
            chat,
            chipStrip,
            composer,
            scrollRef,
            send,
            footer,
          }}
        />
      )}
      {reporting && <ReportDialog slug={slug} t={t} onClose={() => setReporting(false)} />}
    </div>
  );
}

// 'cover' background style: the cover photo, heavily blurred, behind the
// whole page, under a veil of the page colour so text keeps its contrast.
function CoverBackdrop({ page }) {
  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div
        className="absolute -inset-20 bg-cover bg-center opacity-70"
        style={{
          backgroundImage: `url("${brandImageUrl(page.coverImageId)}")`,
          filter: 'blur(64px) saturate(1.15)',
        }}
      />
      <div className="absolute inset-0" style={{ background: 'var(--cover-veil)' }} />
    </div>
  );
}

function Profile({ page, links, t, compact, nameEndRef }) {
  return (
    <>
      <div
        className={`relative flex flex-col items-center text-center ${compact ? 'px-6' : 'px-7 pb-6'}`}
        style={{ marginTop: compact ? -40 : -46 }}
      >
        <Avatar page={page} size={compact ? 80 : 92} ring t={t} />
        {/* Tenant text takes its own direction, whatever the page's. */}
        <h1
          dir="auto"
          className={`font-extrabold leading-tight ${compact ? 'mt-2.5 text-[22px]' : 'mt-3 text-2xl'}`}
        >
          {page.name}
        </h1>
        {page.bio && (
          <p
            dir="auto"
            className={`text-[var(--text-muted)] [text-wrap:balance] ${
              compact
                ? 'mt-1 max-w-[310px] text-sm leading-normal'
                : 'mt-1.5 text-[14.5px] leading-relaxed'
            }`}
          >
            {page.bio}
          </p>
        )}
        {!compact && links.length > 0 && (
          <div className="mt-[18px]">
            <LinkButtons links={links} size={44} surface="var(--surface-2)" />
          </div>
        )}
      </div>
      {nameEndRef && <div ref={nameEndRef} />}
      {compact && links.length > 0 && (
        <div className="mt-4">
          <LinkButtons links={links} />
        </div>
      )}
    </>
  );
}

function MobileLayout({
  page,
  hours,
  links,
  t,
  lang,
  chat,
  chipStrip,
  composer,
  scrollRef,
  send,
  footer,
}) {
  const profileEndRef = useRef(null);
  const [collapsed, setCollapsed] = useState(false);
  const hasExtras = !!(page.bio || links.length);

  // Once the profile has scrolled away, a slim header keeps the business's
  // name and picture on screen. The marker sits right after the name and bio;
  // the scroll box is `relative`, so its offsetTop is measured inside the box.
  const onScroll = (e) => {
    const limit = (profileEndRef.current?.offsetTop ?? 220) - 8;
    const next = e.currentTarget.scrollTop > limit;
    if (next !== collapsed) setCollapsed(next);
  };

  return (
    <div className="relative z-[1] flex h-full flex-col">
      {collapsed && (
        <div className="bp-drop absolute inset-x-0 top-0 z-[5] flex items-center gap-2.5 border-b border-[var(--border)] bg-[var(--surface)] px-4 py-2.5 shadow-[0_6px_16px_-10px_rgba(0,0,0,.25)]">
          <Avatar page={page} size={36} t={t} />
          <div className="flex min-w-0 flex-1 flex-col">
            <span dir="auto" className="truncate text-[15px] font-bold leading-snug">
              {page.name}
            </span>
            <Online t={t} />
          </div>
        </div>
      )}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="bp-no-scrollbar relative flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
      >
        <div className="shrink-0">
          {/* Shorter cover on short phones, so more of the page — and the
              chat — is on the first screen. */}
          <Hero page={page} height="min(132px, 17dvh)" gradientHeight={hasExtras ? 104 : 92} />
          <Profile page={page} links={links} t={t} compact nameEndRef={profileEndRef} />
          <InfoCardCompact page={page} hours={hours} t={t} lang={lang} />
        </div>
        {/* Grows to the bottom so a short chat leaves no seam above the
            typing box. */}
        <section
          aria-label={t('chatRegion', { name: page.name })}
          className="flex flex-1 flex-col gap-2.5 px-3.5 pb-4 pt-[22px]"
          // Fades in instead of starting with a hard edge across the page's
          // colour wash.
          style={{ background: 'linear-gradient(to bottom, transparent, var(--chat-bg) 56px)' }}
        >
          <div className="mb-1 flex items-center gap-2.5 text-xs font-semibold text-[var(--text-muted)]">
            <span className="h-px flex-1 bg-[var(--border)]" />
            {t('ask')}
            <span className="h-px flex-1 bg-[var(--border)]" />
          </div>
          <Messages {...chat} />
        </section>
      </div>
      {chipStrip && (
        <div className="bp-no-scrollbar flex shrink-0 gap-2 overflow-x-auto border-t border-[var(--border)] bg-[var(--surface)] px-3 pt-2">
          {page.suggestedQuestions.map((q) => (
            <Chip key={q} strip onClick={() => send(q)}>
              {q}
            </Chip>
          ))}
        </div>
      )}
      <div
        className={`shrink-0 bg-[var(--surface)] px-3 pb-2 pt-2.5 ${chipStrip ? '' : 'border-t border-[var(--border)]'}`}
      >
        {composer}
      </div>
      <div className="flex shrink-0 items-center justify-center gap-2 bg-[var(--surface)] pb-3 pt-0.5 text-[11px]">
        {footer}
      </div>
    </div>
  );
}

function DesktopLayout({
  page,
  hours,
  links,
  t,
  lang,
  chat,
  chipStrip,
  composer,
  scrollRef,
  send,
  footer,
}) {
  const hasExtras = !!(page.bio || links.length);
  return (
    <div className="relative z-[1] mx-auto grid h-full max-w-[1240px] grid-cols-[380px_minmax(0,1fr)] gap-6 px-8 py-7">
      <aside className="bp-no-scrollbar flex min-h-0 flex-col gap-3.5 overflow-y-auto">
        <div className="overflow-hidden rounded-3xl border border-[var(--border)] bg-[var(--surface)] shadow-[0_18px_40px_-24px_rgba(0,0,0,.25)]">
          <Hero page={page} height={140} gradientHeight={hasExtras ? 104 : 92} />
          <Profile page={page} links={links} t={t} />
          <InfoSections page={page} hours={hours} t={t} lang={lang} />
        </div>
        <div className="flex items-center justify-center gap-2 text-xs">{footer}</div>
      </aside>
      <section
        aria-label={t('chatRegion', { name: page.name })}
        className="flex min-h-0 flex-col overflow-hidden rounded-3xl border border-[var(--border)] bg-[var(--surface)] shadow-[0_18px_40px_-24px_rgba(0,0,0,.25)]"
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-[var(--border)] px-[22px] py-4">
          <Avatar page={page} size={42} t={t} />
          <div className="flex min-w-0 flex-col">
            <span dir="auto" className="truncate text-base font-bold leading-snug">
              {page.name}
            </span>
            <Online t={t} />
          </div>
        </header>
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-y-auto"
          style={{ background: 'var(--chat-bg)' }}
        >
          <div className="mx-auto flex max-w-[720px] flex-col gap-3 px-7 pb-5 pt-7">
            <Messages {...chat} />
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-2.5 border-t border-[var(--border)] px-[22px] pb-[18px] pt-3">
          {chipStrip && (
            <div className="mx-auto flex w-full max-w-[720px] flex-wrap gap-2">
              {page.suggestedQuestions.map((q) => (
                <Chip key={q} strip onClick={() => send(q)}>
                  {q}
                </Chip>
              ))}
            </div>
          )}
          <div className="mx-auto w-full max-w-[720px]">{composer}</div>
        </div>
      </section>
    </div>
  );
}

function Online({ t }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
      <span className="h-[7px] w-[7px] rounded-full bg-[var(--accent-ink)]" />
      {t('online')}
    </span>
  );
}
